import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import RenderLoader from "./components/RenderLoader";
import Privacy from "./screens/Privacy";
import PackageScreen from "./screens/PackageScreen";
import { cityPages, placeholderRoutes } from "./data/seoPages";

const CheckoutScreen = lazy(() => import("./screens/CheckoutScreen"));
const GuestHomeScreen = lazy(() => import("./screens/GuestHomeScreen"));
const ProductScreen = lazy(() => import("./screens/ProductScreen"));
const CartScreen = lazy(() => import("./screens/CartScreen"));
const OrderCompleted = lazy(() => import("./screens/OrderCompleted"));
const LandingPage = lazy(() => import("./screens/LandingPage"));
const Guest = lazy(() => import("./screens/Guest"));
const OrderDriverImages = lazy(() => import("./components/OrderDriverImages"));
const PageNotFoundScreen = lazy(() => import("./screens/PageNotFoundScreen"));
const GuestAddOn = lazy(() => import("./screens/GuestAddOn.js"));
const SavedCartGuest = lazy(() => import("./screens/SavedCartGuest"));
const SMSCompliance = lazy(() => import("./screens/SMSCompliance"));
const OrderTracking = lazy(() => import("./screens/OrderTracking"));
const TermsAndConditions = lazy(() => import("./screens/TermsAndConditions"));
const ContactScreen = lazy(() => import("./screens/ContactScreen"));
const ConciergeOptinPage = lazy(() => import("./screens/ConciergeOptinPage"));
const ConciergePage = lazy(() => import("./screens/ConciergePage"));
const PodcastPage = lazy(() => import("./screens/PodcastPage"));
const DemoPage = lazy(() => import("./screens/DemoPage"));
const DirectBookingConciergeEmbedDemo = lazy(() =>
  import("./screens/DirectBookingConciergeEmbedDemo")
);
const AirbnbGroceryStockingPage = lazy(() => import("./screens/seo/AirbnbGroceryStockingPage"));
const LocationCityPage = lazy(() => import("./screens/seo/LocationCityPage"));
const LocationsHubPage = lazy(() => import("./screens/seo/LocationsHubPage"));
const SeoPlaceholderPage = lazy(() => import("./screens/seo/SeoPlaceholderPage"));
const VacationRentalGroceryDeliveryPage = lazy(() =>
  import("./screens/seo/VacationRentalGroceryDeliveryPage")
);
const PreArrivalGroceriesPage = lazy(() => import("./screens/seo/PreArrivalGroceriesPage"));
const HowItWorksPage = lazy(() => import("./screens/seo/HowItWorksPage"));
const PricingPage = lazy(() => import("./screens/seo/PricingPage"));
const ForPropertyManagersPage = lazy(() => import("./screens/seo/ForPropertyManagersPage"));
const ForEnterprisePropertyManagersPage = lazy(() =>
  import("./screens/seo/ForEnterprisePropertyManagersPage")
);
const ForMidMarketPropertyManagersPage = lazy(() =>
  import("./screens/seo/ForMidMarketPropertyManagersPage")
);
const AirbnbGroceryDeliveryPage = lazy(() => import("./screens/seo/AirbnbGroceryDeliveryPage"));
const IntegrationsPage = lazy(() => import("./screens/seo/IntegrationsPage"));
const StreamlineIntegrationPage = lazy(() => import("./screens/seo/StreamlineIntegrationPage"));
const HospitableIntegrationPage = lazy(() => import("./screens/seo/HospitableIntegrationPage"));
const GuestyIntegrationPage = lazy(() => import("./screens/seo/GuestyIntegrationPage"));
const AkiaIntegrationPage = lazy(() => import("./screens/seo/AkiaIntegrationPage"));
const ForAirbnbHostsPage = lazy(() => import("./screens/seo/ForAirbnbHostsPage"));
const VacationRentalGroceryDeliverySoftwarePage = lazy(() =>
  import("./screens/seo/VacationRentalGroceryDeliverySoftwarePage")
);
const PreArrivalGrocerySoftwarePage = lazy(() =>
  import("./screens/seo/PreArrivalGrocerySoftwarePage")
);
const BestGroceryDeliveryPartnershipsForVacationRentalsPage = lazy(() =>
  import("./screens/seo/BestGroceryDeliveryPartnershipsForVacationRentalsPage")
);
const HowRentalPropertiesOfferGroceryDeliveryServicesPage = lazy(() =>
  import("./screens/seo/HowRentalPropertiesOfferGroceryDeliveryServicesPage")
);
const GuestGroceryProgramRoiPage = lazy(() =>
  import("./screens/seo/GuestGroceryProgramRoiPage")
);
const AirbnbKitchenStockingChecklistForHostsPage = lazy(() =>
  import("./screens/seo/AirbnbKitchenStockingChecklistForHostsPage")
);
const HowToPriceAGuestGroceryProgramPage = lazy(() =>
  import("./screens/seo/HowToPriceAGuestGroceryProgramPage")
);
const HelpHubPage = lazy(() => import("./screens/seo/HelpHubPage"));
const ResourcesHubPage = lazy(() => import("./screens/seo/ResourcesHubPage"));
const ArrivalConfirmationHarness = lazy(() =>
  import("./screens/dev/ArrivalConfirmationHarness")
);


function App() {
  return (
    <div className="overflow-x-hidden">
      <div className="w-full min-h-screen overflow-x-hidden">
        <Suspense fallback={<RenderLoader />}>
          <Routes>
          {/* <Route exact path='/'> <Navigate to="/guest" /> </Route> */}
          <Route path="/" element={<Suspense fallback={<RenderLoader />}><LandingPage /></Suspense>} />
          <Route path="/homeproducts" element={<Suspense fallback={<RenderLoader />}><GuestHomeScreen /></Suspense>} />
          <Route path="/guest/r/:cid" element={<Suspense fallback={<RenderLoader />}><Guest /></Suspense>} />
          <Route path="/guest/:pid" element={<Suspense fallback={<RenderLoader />}><Guest /></Suspense>} />
          <Route path="/product/:id" element={<Suspense fallback={<RenderLoader />}><ProductScreen /></Suspense>} />
          <Route path="/cart" element={<Suspense fallback={<RenderLoader />}><CartScreen /></Suspense>} />
          <Route path="/checkout" element={<Suspense fallback={<RenderLoader />}><CheckoutScreen /></Suspense>} />
          <Route path="/orderCompleted" element={<Suspense fallback={<RenderLoader />}><OrderCompleted /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<RenderLoader />}><Privacy /></Suspense>} />
          <Route path="/package/:pid" element={<Suspense fallback={<RenderLoader />}><PackageScreen /></Suspense>} />
          <Route path="/guest" element={<Suspense fallback={<RenderLoader />}><Guest /></Suspense>} />
          <Route path="/guest/:pid/ebikes" element={<Suspense fallback={<RenderLoader />}><Guest /></Suspense>} />
          <Route path="/order/:oid" element={<Suspense fallback={<RenderLoader />}><OrderDriverImages /></Suspense>} />
          <Route path="/sms" element={<Suspense fallback={<RenderLoader />}><SMSCompliance /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<RenderLoader />}><TermsAndConditions /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<RenderLoader />}><ContactScreen /></Suspense>} />
          <Route path="/concierge" element={<Suspense fallback={<RenderLoader />}><ConciergePage /></Suspense>} />
          <Route path="/concierge-optin" element={<Suspense fallback={<RenderLoader />}><ConciergeOptinPage /></Suspense>} />
          <Route path="/podcast" element={<Suspense fallback={<RenderLoader />}><PodcastPage /></Suspense>} />
          <Route path="/demo" element={<Suspense fallback={<RenderLoader />}><DemoPage /></Suspense>} />
          <Route path="/direct-booking-bellhopt-concierge-embed-demo" element={<Suspense fallback={<RenderLoader />}><DirectBookingConciergeEmbedDemo /></Suspense>} />
          <Route path="/order-tracking/:oid" element={<Suspense fallback={<RenderLoader />}><OrderTracking /></Suspense>} />
          <Route path="/guest/:pid/savedcart/:cartCode" element={<Suspense fallback={<RenderLoader />}><SavedCartGuest /></Suspense>} />
          <Route path="/__dev/arrival-confirmation-harness" element={<Suspense fallback={<RenderLoader />}><ArrivalConfirmationHarness /></Suspense>} />

          <Route path="/airbnb-grocery-stocking" element={<Navigate to="/airbnb-grocery-stocking/" replace />} />
          <Route path="/vacation-rental-grocery-delivery" element={<Navigate to="/vacation-rental-grocery-delivery/" replace />} />
          <Route path="/pre-arrival-groceries" element={<Navigate to="/pre-arrival-groceries/" replace />} />
          <Route path="/how-it-works" element={<Navigate to="/how-it-works/" replace />} />
          <Route path="/pricing" element={<Navigate to="/pricing/" replace />} />
          <Route path="/for/property-managers" element={<Navigate to="/for/property-managers/" replace />} />
          <Route path="/for/enterprise-property-managers" element={<Navigate to="/for/enterprise-property-managers/" replace />} />
          <Route path="/for/mid-market-property-managers" element={<Navigate to="/for/mid-market-property-managers/" replace />} />
          <Route path="/airbnb-grocery-delivery" element={<Navigate to="/airbnb-grocery-delivery/" replace />} />
          <Route path="/integrations" element={<Navigate to="/integrations/" replace />} />
          <Route path="/integrations/streamline" element={<Navigate to="/integrations/streamline/" replace />} />
          <Route path="/integrations/hospitable" element={<Navigate to="/integrations/hospitable/" replace />} />
          <Route path="/integrations/guesty" element={<Navigate to="/integrations/guesty/" replace />} />
          <Route path="/integrations/akia" element={<Navigate to="/integrations/akia/" replace />} />
          <Route path="/for/airbnb-hosts" element={<Navigate to="/for/airbnb-hosts/" replace />} />
          <Route path="/resources/vacation-rental-grocery-delivery-software" element={<Navigate to="/resources/vacation-rental-grocery-delivery-software/" replace />} />
          <Route path="/resources/pre-arrival-grocery-software" element={<Navigate to="/resources/pre-arrival-grocery-software/" replace />} />
          <Route path="/resources/best-grocery-delivery-partnerships-for-vacation-rentals" element={<Navigate to="/resources/best-grocery-delivery-partnerships-for-vacation-rentals/" replace />} />
          <Route path="/resources/how-rental-properties-offer-grocery-delivery-services" element={<Navigate to="/resources/how-rental-properties-offer-grocery-delivery-services/" replace />} />
          <Route path="/resources/guest-grocery-program-roi" element={<Navigate to="/resources/guest-grocery-program-roi/" replace />} />
          <Route path="/resources/airbnb-kitchen-stocking-checklist-for-hosts" element={<Navigate to="/resources/airbnb-kitchen-stocking-checklist-for-hosts/" replace />} />
          <Route path="/resources/how-to-price-a-guest-grocery-program" element={<Navigate to="/resources/how-to-price-a-guest-grocery-program/" replace />} />
          <Route path="/locations" element={<Navigate to="/locations/" replace />} />
          <Route path="/help" element={<Navigate to="/help/" replace />} />
          <Route path="/resources" element={<Navigate to="/resources/" replace />} />

          <Route path="/airbnb-grocery-stocking/" element={<AirbnbGroceryStockingPage />} />
          <Route path="/vacation-rental-grocery-delivery/" element={<VacationRentalGroceryDeliveryPage />} />
          <Route path="/pre-arrival-groceries/" element={<PreArrivalGroceriesPage />} />
          <Route path="/how-it-works/" element={<HowItWorksPage />} />
          <Route path="/pricing/" element={<PricingPage />} />
          <Route path="/for/property-managers/" element={<ForPropertyManagersPage />} />
          <Route path="/for/enterprise-property-managers/" element={<ForEnterprisePropertyManagersPage />} />
          <Route path="/for/mid-market-property-managers/" element={<ForMidMarketPropertyManagersPage />} />
          <Route path="/airbnb-grocery-delivery/" element={<AirbnbGroceryDeliveryPage />} />
          <Route path="/integrations/" element={<IntegrationsPage />} />
          <Route path="/integrations/streamline/" element={<StreamlineIntegrationPage />} />
          <Route path="/integrations/hospitable/" element={<HospitableIntegrationPage />} />
          <Route path="/integrations/guesty/" element={<GuestyIntegrationPage />} />
          <Route path="/integrations/akia/" element={<AkiaIntegrationPage />} />
          <Route path="/for/airbnb-hosts/" element={<ForAirbnbHostsPage />} />
          <Route path="/resources/vacation-rental-grocery-delivery-software/" element={<VacationRentalGroceryDeliverySoftwarePage />} />
          <Route path="/resources/pre-arrival-grocery-software/" element={<PreArrivalGrocerySoftwarePage />} />
          <Route path="/resources/best-grocery-delivery-partnerships-for-vacation-rentals/" element={<BestGroceryDeliveryPartnershipsForVacationRentalsPage />} />
          <Route path="/resources/how-rental-properties-offer-grocery-delivery-services/" element={<HowRentalPropertiesOfferGroceryDeliveryServicesPage />} />
          <Route path="/resources/guest-grocery-program-roi/" element={<GuestGroceryProgramRoiPage />} />
          <Route path="/resources/airbnb-kitchen-stocking-checklist-for-hosts/" element={<AirbnbKitchenStockingChecklistForHostsPage />} />
          <Route path="/resources/how-to-price-a-guest-grocery-program/" element={<HowToPriceAGuestGroceryProgramPage />} />
          <Route path="/locations/" element={<LocationsHubPage />} />
          <Route path="/help/" element={<HelpHubPage />} />
          <Route path="/resources/" element={<ResourcesHubPage />} />
          {placeholderRoutes.map((path) => (
            <Route key={path} path={path} element={<SeoPlaceholderPage path={path} />} />
          ))}
          {cityPages.map((cityItem) => (
            <Route
              key={cityItem.city}
              path={`/locations/${cityItem.city}/airbnb-grocery-delivery`}
              element={<Navigate to={`/locations/${cityItem.city}/airbnb-grocery-delivery/`} replace />}
            />
          ))}
          {cityPages.map((cityItem) => (
            <Route
              key={`${cityItem.city}-slash`}
              path={`/locations/${cityItem.city}/airbnb-grocery-delivery/`}
              element={<LocationCityPage cityConfig={cityItem} />}
            />
          ))}
          {/* <Route path="/support" element={<SupportScreen />} /> */}
          <Route path="/guest/:pid/addon/:orderAddOnCode" element={<Suspense fallback={<RenderLoader />}><GuestAddOn /></Suspense>} />
          <Route path="*" element={<PageNotFoundScreen />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}

export default App;
